import React, { useEffect, useMemo, useState } from 'react';
import {
  LayoutGrid, Grid3x3, TrendingUp, ListTree, ArrowRight, Trophy, AlertTriangle, Info, Loader2,
} from 'lucide-react';
import { AnalyticsStudentRecord, FoundationCompetencyAnalyticsPayload } from '../../../services/holisticApi';
import {
  computeFLNClassSummary, FLNClassSummary, FLNDomainSummary, LakshyaStats,
} from '../data/flnUtils';
import { computeFLNSummariesFromCounts } from '../data/foundationAnalyticsFromCounts';
import { FLNDomain, FLN_DOMAINS, FLN_CLASS_CONFIGS, FLNLakshya } from '../data/flnSchema';
import { getFoundationCompetencyLevel } from '../../../services/holisticApi';
import { FOUNDATIONAL_SCHEMA } from '../data/foundationalSchema';
import { LEVEL_COLORS } from '../data/competencyExplorerUtils';
import { MovementBadge, Segmented, fmtCount, CalcInfo } from './foundationShared';

const DOMAIN_META: Record<FLNDomain, { color: string; icon: string; soft: string }> = {
  'Numeracy':      { color: '#6366F1', icon: '🔢', soft: 'from-indigo-50 to-violet-50' },
  'Oral Language': { color: '#F59E0B', icon: '🗣️', soft: 'from-amber-50 to-orange-50' },
  'Reading':       { color: '#3B82F6', icon: '📖', soft: 'from-blue-50 to-sky-50' },
  'Writing':       { color: '#10B981', icon: '✍️', soft: 'from-emerald-50 to-teal-50' },
};

const pctColor = (p: number) => LEVEL_COLORS[getFoundationCompetencyLevel(p)];

// competency key (C_8_3) → NCF competency label, built once from the schema
const COMPETENCY_LABEL: Record<string, string> = {};
FOUNDATIONAL_SCHEMA.forEach(cg => cg.assessment.forEach(ind => { COMPETENCY_LABEL[ind.entryKey] = ind.entryLabel; }));
const fmtCompCode = (key: string) => key.replace('C_', 'C-').replace('_', '.');

type ViewKey = 'overview' | 'matrix' | 'movement' | 'domains';

interface Props {
  records: AnalyticsStudentRecord[];
  classId?: number;
  /** Per-competency rubric analytics (national/region/school) when records lack rubric maps. */
  analytics?: FoundationCompetencyAnalyticsPayload;
  /** true at national/region/school — build from aggregated counts, never scan records. */
  preferCounts?: boolean;
  /** the analytics query is still in flight. */
  loading?: boolean;
}

export const FLNExplorer: React.FC<Props> = ({ records, classId, analytics, preferCounts = false, loading = false }) => {
  const summaries = useMemo(() => {
    // Higher levels: no per-student rubric in records — build Lakshya stats straight
    // from the aggregated counts and never iterate the large records array.
    if (preferCounts) return computeFLNSummariesFromCounts(analytics, classId);
    // Class level: per-student rubric records for the one class (classId is always set here).
    const s = classId !== undefined ? computeFLNClassSummary(records, classId) : null;
    return s ? [s] : [];
  }, [preferCounts, records, classId, analytics]);

  const [view, setView] = useState<ViewKey>('overview');
  const [classIdx, setClassIdx] = useState(0);

  // Keep the selected class in range when the payload changes (e.g. year switch shrinks the class set).
  useEffect(() => {
    setClassIdx(i => (i > summaries.length - 1 ? 0 : i));
  }, [summaries.length]);

  if (loading && !summaries.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-700">
        <Loader2 size={26} className="animate-spin text-indigo-500" />
        <p className="text-sm font-medium">Loading FLN Lakshya analytics…</p>
      </div>
    );
  }

  if (!summaries.length) {
    return <FLNMappingReference classId={classId} />;
  }

  const active = summaries[classIdx] ?? summaries[0];
  const allLakshyas = active.domains.flatMap(d => d.lakshyas);

  return (
    <div className="space-y-4">
      {/* View switcher + class chips */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Segmented<ViewKey>
            size="sm"
            value={view}
            onChange={setView}
            options={[
              { key: 'overview', label: 'Overview',       icon: <LayoutGrid size={13} /> },
              { key: 'matrix',   label: 'Lakshya Matrix',  icon: <Grid3x3 size={13} /> },
              { key: 'movement', label: 'Movement',        icon: <TrendingUp size={13} /> },
              { key: 'domains',  label: 'By Domain',       icon: <ListTree size={13} /> },
            ]}
          />
          <CalcInfo
            align="left"
            title="How FLN Entry / Exit is calculated"
            lines={[
              'Each FLN Lakshya maps to one NCF competency (e.g. BNX1 → C-8.3).',
              'A Lakshya is “achieved” when a student is Proficient (rubric 3) on that competency.',
              'Entry % / Exit % = the share of students Proficient at Entry (Term 1) vs Exit (Term 2).',
              'Domain and Overall % = the simple average of the Lakshyas’ proficiency %. Movement = Exit % − Entry %.',
              'Only Proficient counts here, so FLN % is stricter (usually lower) than the Competency tab’s domain %.',
            ]}
          />
        </div>
        {summaries.length > 1 && (
          <div className="flex gap-1.5 overflow-x-auto">
            {summaries.map((s, i) => (
              <button
                key={s.classId}
                onClick={() => setClassIdx(i)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition ${
                  i === classIdx ? 'bg-indigo-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s.classLabel}
              </button>
            ))}
          </div>
        )}
      </div>

      {view === 'overview' && <OverviewView summary={active} lakshyas={allLakshyas} />}
      {view === 'matrix'   && <MatrixView summary={active} />}
      {view === 'movement' && <MovementView lakshyas={allLakshyas} />}
      {view === 'domains'  && <DomainsView summary={active} />}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  OVERVIEW
// ══════════════════════════════════════════════════════════════════════════════
const OverviewView: React.FC<{ summary: FLNClassSummary; lakshyas: LakshyaStats[] }> = ({ summary, lakshyas }) => {
  const withData = lakshyas.filter(l => l.exitTotal > 0);
  const gainers = [...withData].sort((a, b) => b.movementPct - a.movementPct).slice(0, 3);
  const attention = [...withData].sort((a, b) => a.exitProficientPct - b.exitProficientPct).slice(0, 3);
  const achieved = withData.filter(l => l.exitProficientPct >= 83).length;

  return (
    <div className="space-y-4">
      {/* Big Entry → Exit banner */}
      <div className="rounded-2xl border border-gray-200 bg-gradient-to-r from-slate-50 via-white to-indigo-50 p-5">
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">{summary.classLabel}</p>
            <p className="text-[13px] text-gray-600 mt-0.5">{fmtCount(summary.totalStudents)} students · {lakshyas.length} Lakshyas</p>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <StatBlock label="Entry Proficient" value={`${summary.entryProficientPct}%`} color="#64748B" />
            <ArrowRight size={20} className="text-gray-500" />
            <StatBlock label="Exit Proficient" value={`${summary.exitProficientPct}%`} color="#6366F1" />
            <div className="pl-4 border-l border-gray-200">
              <p className="text-[11px] font-semibold text-gray-600">MOVEMENT</p>
              <MovementBadge delta={summary.movementPct} size="md" />
            </div>
            <div className="pl-4 border-l border-gray-200 text-center">
              <p className="text-[11px] font-semibold text-gray-600">LAKSHYAS MET</p>
              <p className="text-sm font-bold text-emerald-600">{achieved}<span className="text-gray-500 font-normal">/{withData.length}</span></p>
            </div>
          </div>
        </div>
      </div>

      {/* Domain cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {summary.domains.map(d => {
          const meta = DOMAIN_META[d.domain];
          return (
            <div key={d.domain} className={`rounded-xl border border-gray-200 p-4 bg-gradient-to-br ${meta.soft}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{meta.icon}</span>
                <span className="text-xs font-bold text-gray-700">{d.domain}</span>
              </div>
              <div className="flex items-end gap-2 mb-2">
                <span className="text-2xl font-black" style={{ color: pctColor(d.exitProficientPct) }}>{d.exitProficientPct}%</span>
                <span className="text-[12px] text-gray-600 mb-1">exit proficient</span>
              </div>
              <div className="flex items-center gap-2 text-[12px] text-gray-700">
                <span>Entry {d.entryProficientPct}%</span>
                <ArrowRight size={10} className="text-gray-500" />
                <MovementBadge delta={d.movementPct} />
                <span className="ml-auto text-gray-600">{d.lakshyas.length} codes</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Gainers / Attention */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <CalloutCard
          title="Top Movers" icon={<Trophy size={14} className="text-emerald-500" />} tone="emerald"
          rows={gainers.map(l => ({ code: l.shortLabel, label: l.fullLabel, right: <MovementBadge delta={l.movementPct} />, sub: `${l.entryProficientPct}% → ${l.exitProficientPct}%` }))}
        />
        <CalloutCard
          title="Needs Attention" icon={<AlertTriangle size={14} className="text-rose-500" />} tone="rose"
          rows={attention.map(l => ({ code: l.shortLabel, label: l.fullLabel, right: <span className="text-xs font-bold" style={{ color: pctColor(l.exitProficientPct) }}>{l.exitProficientPct}%</span>, sub: `${fmtCount(l.exitProficient)}/${fmtCount(l.exitTotal)} proficient at exit` }))}
        />
      </div>
    </div>
  );
};

const StatBlock: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <div className="text-center">
    <p className="text-[11px] font-semibold text-gray-600">{label.toUpperCase()}</p>
    <p className="text-xl font-black" style={{ color }}>{value}</p>
  </div>
);

const CalloutCard: React.FC<{
  title: string; icon: React.ReactNode; tone: 'emerald' | 'rose';
  rows: { code: string; label: string; right: React.ReactNode; sub: string }[];
}> = ({ title, icon, rows }) => (
  <div className="rounded-xl border border-gray-200 p-4">
    <div className="flex items-center gap-2 mb-3">{icon}<span className="text-xs font-bold text-gray-700">{title}</span></div>
    <div className="space-y-2">
      {rows.length === 0 && <p className="text-[13px] text-gray-500 italic">No data</p>}
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="px-2 py-0.5 rounded-md text-[12px] font-bold bg-gray-100 text-gray-600 shrink-0 w-14 text-center">{r.code}</span>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-gray-700 truncate">{r.label}</p>
            <p className="text-[11px] text-gray-600">{r.sub}</p>
          </div>
          <div className="shrink-0">{r.right}</div>
        </div>
      ))}
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
//  LAKSHYA MATRIX
// ══════════════════════════════════════════════════════════════════════════════
const MatrixView: React.FC<{ summary: FLNClassSummary }> = ({ summary }) => (
  <div className="space-y-4">
    <p className="text-[13px] text-gray-600">
      Each tile shows a Lakshya code with its Entry → Exit proficiency. Border colour reflects exit achievement level.
    </p>
    {summary.domains.map(domain => {
      const meta = DOMAIN_META[domain.domain];
      const eng = domain.lakshyas.filter(l => !l.isHindi);
      const hin = domain.lakshyas.filter(l => l.isHindi);
      return (
        <div key={domain.domain}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-base">{meta.icon}</span>
            <span className="text-xs font-bold text-gray-700">{domain.domain}</span>
            <span className="text-[12px] text-gray-600">Entry {domain.entryProficientPct}% → Exit {domain.exitProficientPct}%</span>
            <MovementBadge delta={domain.movementPct} />
          </div>
          {eng.length > 0 && <LakshyaTileGrid lakshyas={eng} />}
          {hin.length > 0 && (
            <>
              <p className="text-[11px] font-bold text-amber-600 uppercase tracking-wide mt-2 mb-1">Hindi</p>
              <LakshyaTileGrid lakshyas={hin} />
            </>
          )}
        </div>
      );
    })}
  </div>
);

const LakshyaTileGrid: React.FC<{ lakshyas: LakshyaStats[] }> = ({ lakshyas }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-2">
    {lakshyas.map(l => {
      const hasData = l.exitTotal > 0 || l.entryTotal > 0;
      const accent = pctColor(l.exitProficientPct);
      return (
        <div
          key={l.code}
          title={l.fullLabel}
          className="rounded-lg border bg-white p-2.5 transition hover:shadow-sm"
          style={{ borderColor: hasData ? `${accent}55` : '#e5e7eb' }}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[13px] font-black text-gray-700">{l.shortLabel}</span>
            {hasData ? <MovementBadge delta={l.movementPct} /> : <span className="text-[11px] text-gray-500">n/a</span>}
          </div>
          {/* entry */}
          <div className="flex items-center gap-1 mb-0.5">
            <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${l.entryProficientPct}%`, background: '#CBD5E1' }} />
            </div>
            <span className="text-[10px] text-gray-600 w-6 text-right">{hasData ? `${l.entryProficientPct}%` : '—'}</span>
          </div>
          {/* exit */}
          <div className="flex items-center gap-1">
            <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${l.exitProficientPct}%`, background: accent }} />
            </div>
            <span className="text-[10px] font-semibold w-6 text-right" style={{ color: accent }}>{hasData ? `${l.exitProficientPct}%` : '—'}</span>
          </div>
        </div>
      );
    })}
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
//  MOVEMENT (ranked Entry → Exit progress)
// ══════════════════════════════════════════════════════════════════════════════
const MovementView: React.FC<{ lakshyas: LakshyaStats[] }> = ({ lakshyas }) => {
  const ranked = [...lakshyas.filter(l => l.exitTotal > 0 || l.entryTotal > 0)].sort((a, b) => b.movementPct - a.movementPct);
  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-bold text-gray-700">Entry → Exit progress by Lakshya</p>
        <div className="flex items-center gap-3 text-[12px] text-gray-600">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#CBD5E1' }} />Entry</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-500" />Exit</span>
        </div>
      </div>
      <div className="space-y-2.5">
        {ranked.map(l => (
          <div key={l.code} className="flex items-center gap-3">
            <span className="text-[12px] font-bold text-gray-600 w-14 shrink-0">{l.shortLabel}</span>
            <div className="flex-1 relative h-5">
              {/* track */}
              <div className="absolute inset-y-0 left-0 right-0 my-auto h-2 bg-gray-100 rounded-full" />
              {/* exit fill */}
              <div className="absolute inset-y-0 left-0 my-auto h-2 rounded-full bg-indigo-500 transition-all duration-500" style={{ width: `${l.exitProficientPct}%` }} />
              {/* entry marker */}
              <div className="absolute inset-y-0 my-auto w-0.5 h-4 bg-slate-400" style={{ left: `${l.entryProficientPct}%` }} title={`Entry ${l.entryProficientPct}%`} />
            </div>
            <span className="text-[12px] text-gray-600 w-24 text-right shrink-0">{l.entryProficientPct}% → <span className="font-bold text-indigo-600">{l.exitProficientPct}%</span></span>
            <span className="w-14 text-right shrink-0"><MovementBadge delta={l.movementPct} /></span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  BY DOMAIN (detailed accordion)
// ══════════════════════════════════════════════════════════════════════════════
const DomainsView: React.FC<{ summary: FLNClassSummary }> = ({ summary }) => (
  <div className="space-y-2">
    {summary.domains.map((d, i) => <DomainAccordion key={d.domain} domain={d} defaultOpen={i === 0} />)}
  </div>
);

const DomainAccordion: React.FC<{ domain: FLNDomainSummary; defaultOpen?: boolean }> = ({ domain, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const meta = DOMAIN_META[domain.domain];
  const eng = domain.lakshyas.filter(l => !l.isHindi);
  const hin = domain.lakshyas.filter(l => l.isHindi);
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition" onClick={() => setOpen(o => !o)}>
        <span className="text-base">{meta.icon}</span>
        <span className="text-sm font-semibold text-gray-800 flex-1 text-left">{domain.domain}</span>
        <span className="text-[13px] text-gray-600">Entry {domain.entryProficientPct}%</span>
        <ArrowRight size={12} className="text-gray-500" />
        <span className="text-[13px] font-bold text-indigo-600">Exit {domain.exitProficientPct}%</span>
        <MovementBadge delta={domain.movementPct} />
        <span className="text-gray-600 text-xs ml-1">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="border-t border-gray-100 px-4 py-3">
          {eng.length > 0 && <LakshyaDetailRows lakshyas={eng} heading={hin.length > 0 ? 'English' : undefined} />}
          {hin.length > 0 && <LakshyaDetailRows lakshyas={hin} heading="Hindi" hindi />}
        </div>
      )}
    </div>
  );
};

const LakshyaDetailRows: React.FC<{ lakshyas: LakshyaStats[]; heading?: string; hindi?: boolean }> = ({ lakshyas, heading, hindi }) => (
  <div className={heading ? 'mt-2 first:mt-0' : ''}>
    {heading && <p className={`text-[12px] font-bold uppercase tracking-wide mb-1.5 ${hindi ? 'text-amber-600' : 'text-indigo-500'}`}>{heading}</p>}
    <div className="space-y-1">
      {lakshyas.map(l => {
        const has = l.entryTotal > 0 || l.exitTotal > 0;
        return (
          <div key={l.code} className="flex items-center gap-3 py-1">
            <span className="text-[13px] font-bold text-gray-600 w-14 shrink-0">{l.shortLabel}</span>
            <span className="text-[12px] text-gray-600 flex-1 truncate">{l.fullLabel.split('–')[1]?.trim() ?? l.fullLabel}</span>
            <div className="w-40 shrink-0 space-y-0.5">
              <MiniBar label="E" pct={l.entryProficientPct} color="#CBD5E1" has={has} />
              <MiniBar label="X" pct={l.exitProficientPct} color="#6366F1" has={has} />
            </div>
            <span className="w-12 text-right shrink-0"><MovementBadge delta={l.movementPct} /></span>
          </div>
        );
      })}
    </div>
  </div>
);

const MiniBar: React.FC<{ label: string; pct: number; color: string; has: boolean }> = ({ label, pct, color, has }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-[10px] text-gray-600 w-2.5">{label}</span>
    <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
    </div>
    <span className="text-[10px] font-semibold text-gray-700 w-7 text-right">{has ? `${pct}%` : '—'}</span>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
//  FLN ↔ COMPETENCY MAPPING (shown when rubric-level Entry/Exit data isn't in the
//  payload — national/region/school. Lets the user browse the full framework:
//  every Lakshya code mapped to its NCF competency, by class / domain / language.)
// ══════════════════════════════════════════════════════════════════════════════
const FLNMappingReference: React.FC<{ classId?: number }> = ({ classId }) => {
  const configs = classId !== undefined
    ? FLN_CLASS_CONFIGS.filter(c => c.classIds.includes(classId))
    : FLN_CLASS_CONFIGS;
  const [gi, setGi] = useState(0);
  const cfg = configs[gi] ?? configs[0];

  if (!cfg) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <Info size={15} className="text-amber-500 mt-0.5 shrink-0" />
        <p className="text-[13px] text-amber-700 leading-relaxed">
          Showing the <span className="font-semibold">FLN Lakshya ↔ competency mapping</span>. Entry vs Exit
          achievement percentages are computed from per-student rubric records, available at
          <span className="font-semibold"> class level</span> — drill into a class from the navigation below to
          populate the live numbers.
        </p>
      </div>

      {/* class-group selector */}
      {configs.length > 1 && (
        <div className="flex gap-1.5 flex-wrap">
          {configs.map((c, i) => (
            <button
              key={c.group}
              onClick={() => setGi(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                i === gi ? 'bg-indigo-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {FLN_DOMAINS.map(domain => {
        const inDomain = cfg.lakshyas.filter(l => l.domain === domain);
        if (!inDomain.length) return null;
        const meta = DOMAIN_META[domain];
        const eng = inDomain.filter(l => !l.isHindi);
        const hin = inDomain.filter(l => l.isHindi);
        return (
          <div key={domain} className="rounded-xl border border-gray-200 overflow-hidden">
            <div className={`flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r ${meta.soft}`}>
              <span className="text-base">{meta.icon}</span>
              <span className="text-sm font-bold text-gray-700">{domain}</span>
              <span className="ml-auto text-[12px] text-gray-700 font-medium">{inDomain.length} Lakshyas</span>
            </div>
            <div className="px-4 py-3">
              {eng.length > 0 && <MappingRows lakshyas={eng} accent={meta.color} heading={hin.length > 0 ? 'English' : undefined} />}
              {hin.length > 0 && <MappingRows lakshyas={hin} accent={meta.color} heading="Hindi" hindi />}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MappingRows: React.FC<{ lakshyas: FLNLakshya[]; accent: string; heading?: string; hindi?: boolean }> = ({ lakshyas, accent, heading, hindi }) => (
  <div className={heading ? 'mt-3 first:mt-0' : ''}>
    {heading && <p className={`text-[12px] font-bold uppercase tracking-wide mb-1.5 ${hindi ? 'text-amber-600' : 'text-indigo-500'}`}>{heading}</p>}
    <div className="space-y-1">
      {lakshyas.map(l => (
        <div key={l.code} className="flex items-center gap-2.5 py-1.5 border-b border-gray-50 last:border-0">
          <span className="px-2 py-0.5 rounded-md text-[12px] font-bold text-white shrink-0 w-16 text-center" style={{ background: accent }}>
            {l.shortLabel}
          </span>
          <ArrowRight size={12} className="text-gray-500 shrink-0" />
          <span className="text-[12px] font-bold text-gray-700 shrink-0 w-14">{fmtCompCode(l.competencyKey)}</span>
          <span className="text-[13px] text-gray-600 flex-1 min-w-0 truncate" title={COMPETENCY_LABEL[l.competencyKey] || ''}>
            {COMPETENCY_LABEL[l.competencyKey] || l.fullLabel.split('–')[1]?.trim()}
          </span>
        </div>
      ))}
    </div>
  </div>
);
